/**
 * EntitySelectorTable — Reusable checkbox entity picker for drawers.
 *
 * Renders a searchable list with checkboxes for selecting entities
 * (area groups, resident groups, devices, user groups, users, contacts).
 *
 * Styled to match the Report Settings filter sections, under its own cf-filter-*
 * names (careflow-page.css).
 *
 * Props:
 *   data              — full array of entity records
 *   columns           — [{ key, label, align }] column definitions
 *   selectedIds       — array of selected ID strings
 *   onSelectionChange — callback receiving updated ID array
 *   searchKeys        — array of keys to search on
 *   idKey             — primary key name (e.g. "area_group_id")
 *   title             — optional section header text
 */

import { useState, useMemo } from 'react';
import { formatPhone } from '../../../../libs.jsx';


const PHONE_KEYS = new Set(['phone', 'contact_phone']);

const EntitySelectorTable = ({
    data = [],
    columns = [],
    selectedIds = [],
    onSelectionChange,
    searchKeys = [],
    idKey,
    title,
}) => {
    const [search, setSearch] = useState('');
    const [showSelected, setShowSelected] = useState(false);
    const [sorter, setSorter] = useState({ col: columns[0]?.key || '', dir: 'asc' });

    // Filter + sort
    const filtered = useMemo(() => {
        let items = data;

        // Search filter
        if (search.trim()) {
            const q = search.toLowerCase();
            items = items.filter((row) =>
                searchKeys.some((k) => String(row[k] || '').toLowerCase().includes(q))
            );
        }

        // Show selected only
        if (showSelected) {
            items = items.filter((row) => selectedIds.includes(String(row[idKey])));
        }

        // Sort
        if (sorter.col) {
            items = [...items].sort((a, b) => {
                const aVal = String(a[sorter.col] || '').toLowerCase();
                const bVal = String(b[sorter.col] || '').toLowerCase();
                const cmp = aVal.localeCompare(bVal);
                return sorter.dir === 'asc' ? cmp : -cmp;
            });
        }

        return items;
    }, [data, search, showSelected, selectedIds, sorter, searchKeys, idKey]);

    const handleToggle = (id) => {
        const idStr = String(id);
        const next = selectedIds.includes(idStr)
            ? selectedIds.filter((x) => x !== idStr)
            : [...selectedIds, idStr];
        onSelectionChange(next);
    };

    const handleSelectAll = () => {
        const currentSet = new Set(selectedIds);
        filtered.forEach((r) => currentSet.add(String(r[idKey])));
        onSelectionChange([...currentSet]);
    };

    const handleClear = () => {
        const visibleIds = new Set(filtered.map((r) => String(r[idKey])));
        onSelectionChange(selectedIds.filter((id) => !visibleIds.has(id)));
    };

    const handleSort = (col) => {
        setSorter((prev) =>
            prev.col === col
                ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { col, dir: 'asc' }
        );
    };

    // Build the display string for each row from columns
    const getRowDisplay = (row) => {
        return columns.map((col) => row[col.key] ?? '\u2014').filter(Boolean);
    };

    const selectedCount = selectedIds.length;

    return (
        <div className="cf-filter-section">
            {/* Header with title and action links */}
            <div className="cf-filter-section__header">
                <span className="cf-filter-section__label">
                    {title || columns[0]?.label || 'Select'}
                    {selectedCount > 0 ? (
                        <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.375rem' }}>
                            ({selectedCount})
                        </span>
                    ) : null}
                </span>
                <div className="cf-filter-section__actions">
                    <button type="button" className="cf-filter-section__btn" onClick={handleSelectAll}>
                        Select All
                    </button>
                    <button type="button" className="cf-filter-section__btn" onClick={handleClear}>
                        Clear
                    </button>
                    <button
                        type="button"
                        className={`cf-filter-section__btn${showSelected ? ' cf-filter-section__btn--active' : ''}`}
                        onClick={() => setShowSelected((p) => !p)}
                    >
                        Selected
                    </button>
                </div>
            </div>

            {/* Search */}
            <input
                type="search"
                className="cf-filter-section__search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            {/* Column sort headers */}
            {columns.length > 1 ? (
                <div className="cf-entity-col-headers">
                    <span className="cf-entity-col-headers__check" />
                    {columns.map((col) => (
                        <span
                            key={col.key}
                            className="cf-entity-col-headers__col"
                            onClick={() => handleSort(col.key)}
                        >
                            {col.label}
                            {sorter.col === col.key ? (
                                <span style={{ marginLeft: '0.2rem', fontSize: '0.6rem' }}>
                                    {sorter.dir === 'asc' ? '\u25B2' : '\u25BC'}
                                </span>
                            ) : null}
                        </span>
                    ))}
                </div>
            ) : null}

            {/* Scrollable list */}
            <div className="cf-filter-list">
                {filtered.length === 0 ? (
                    <div className="cf-filter-list__empty">
                        {data.length === 0 ? 'No data available' : 'No results found'}
                    </div>
                ) : (
                    filtered.map((row) => {
                        const rowId = String(row[idKey]);
                        const isChecked = selectedIds.includes(rowId);
                        const displayParts = getRowDisplay(row);

                        return (
                            <label
                                key={rowId}
                                className={`cf-filter-list__item${columns.length > 1 ? ' cf-filter-list__item--multi-col' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggle(row[idKey])}
                                />
                                {columns.length === 1 ? (
                                    <span className="cf-filter-list__item-name">
                                        {displayParts[0]}
                                    </span>
                                ) : (
                                    <span className="cf-filter-list__item-content cf-filter-list__item-content--row">
                                        {columns.map((col) => (
                                            <span
                                                key={col.key}
                                                className={col.muted ? 'cf-filter-list__item-subtext' : 'cf-filter-list__item-name'}
                                            >
                                                {PHONE_KEYS.has(col.key) ? (formatPhone(row[col.key]) ?? '\u2014') : (row[col.key] ?? '\u2014')}
                                            </span>
                                        ))}
                                    </span>
                                )}
                            </label>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default EntitySelectorTable;
